import HBIcon from "@/components/HBIcon";
import HBMessage from "@/components/HBMessage";
import Image from "@/components/Image";
import {
  isCompanyVipByUser,
  isPersonalVipByUser,
  isSingleVipByUser,
} from "@/constants/vip";
import useAppStore from "@/context/store";
import { gdImgSrcPolicy } from "@/utils/imageUrl";
import { getDiffDays } from "@/utils/time";
import useRequest from "@/utils/useReq";
import { Skeleton } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { round } from "lodash";
import { useCallback, useContext, useMemo, useState } from "react";
import { getGrocery, getVipGoods, orderCalculate } from "../../../services";
import CompanyTab from "../buyDialog/companyTab";
import PayBottom from "../pay/payBottom";
import { VipDialogContext } from "../shared/configContext";
import {
  getLastExpireTime,
  getMaxBuyNumber,
  getPayDetailByGood,
  getSkuUnit,
  isTemPkg,
  isUpgradePkg,
  isUpgradePlusPkg,
  parseSkuDuration,
} from "../shared/utils";
import { VipTab } from "../shared/vipTab";
import styles from "./index.module.less";
dayjs.extend(duration);

// 根据时长匹配对应的升级包
function getMatchSku(good, diff) {
  // 排序，根据时长 由短到长进行排序
  const skus = good.skus
    .map((sku) => {
      const parse = parseSkuDuration(sku);
      let days = dayjs.duration(parse.num, parse.unit).asDays();

      // 数据校准，去周期时间最大天数，避免计算价格时出现落差
      if (days === 30 || days === 60) {
        days += 1;
      }

      if (days === 90) {
        days += 2;
      }

      return {
        ...sku,
        durationDays: days,
      };
    })
    .sort((a, b) => {
      return a.durationDays - b.durationDays;
    });

  for (let i = 0; i < skus.length; i++) {
    const sku = skus[i];
    if (sku.durationDays >= diff) {
      return sku;
    }
  }

  return skus[skus.length - 1];
}

export default function PkgDetail({
  userRight,
  defaultSelectedPkg,
  currentVipType = "personal",
  onOrderDone,
  onCancel,
  onTransfer,
  ...rest
} = {}) {
  const [disabled, setDisabled] = useState(false);
  const onDisableChange = useCallback((state) => {
    setDisabled(state);
  }, []);
  const vipGoods = useRequest(getVipGoods);
  const { config } = useContext(VipDialogContext);
  const user = useAppStore((s) => s.user);
  const [currentVip, setCurrentVip] = useState(currentVipType);
  // 弹框配置
  const { data: dialogConf } = useRequest(async () =>
    getGrocery("increate-times-dialog	")
  );

  if (!config || !userRight || vipGoods.loading) {
    // 占位元素
    return <div className={styles.detailsBlock}></div>;
  }

  return (
    <div {...rest} className={styles.details}>
      {isPersonalVipByUser(user) && (
        <VipTab
          conf={config}
          currentVip={currentVip}
          setCurrentVip={setCurrentVip}
          disabled={disabled}
        />
      )}

      <div className={styles.container}>
        {currentVipType === "personal" ? (
          currentVip === "personal" ? (
            <IncreateTimesTab
              goods={vipGoods.data.list}
              vipType={currentVipType}
              conf={dialogConf}
              onUpdate={() => setCurrentVip("company")}
              onOrderDone={onOrderDone}
              onDisableChange={onDisableChange}
              defaultSelectedPkg={defaultSelectedPkg}
              onTransfer={onTransfer}
            />
          ) : (
            <CompanyTab
              isUpgrade={true}
              goods={vipGoods.data.list}
              onOrderDone={onOrderDone}
              onDisableChange={onDisableChange}
              onTransfer={onTransfer}
            />
          )
        ) : (
          <IncreateTimesTab
            goods={vipGoods.data.list}
            vipType={currentVipType}
            conf={dialogConf}
            onOrderDone={onOrderDone}
            onCancel={onCancel}
            onDisableChange={onDisableChange}
            defaultSelectedPkg={defaultSelectedPkg}
            onTransfer={onTransfer}
          />
        )}
      </div>
    </div>
  );
}

function IncreateTimesTab({
  goods,
  vipType,
  conf,
  defaultSelectedPkg,
  onUpdate,
  onOrderDone,
  onCancel,
  onDisableChange,
  onTransfer,
}) {
  const { rightData } = useContext(VipDialogContext);
  const [disabled, setDisabled] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState();
  const onDisableChangeWrap = useCallback(
    (state) => {
      setDisabled(state);
      onDisableChange && onDisableChange(state);
    },
    [onDisableChange]
  );
  const user = useAppStore((s) => s.user);

  // 处理显示的卡片数据，部分配置，部分需要计算
  const { data: cardList } = useRequest(
    async () => {
      if (conf && rightData) {
        const result = [];

        for (let i = 0; i < conf[vipType].length; i++) {
          const item = conf[vipType][i];
          const good = goods.find((g) => g.code === item.goodCode);
          const sku = good.skus[0];
          const right_package_code = sku.right_packages[0].identification;
          const matchRights = rightData.effect_rights.filter(
            (right) => right_package_code === right.right_package_code
          );

          const obj = {
            ...item,
            sku,
          };

          if (isTemPkg(sku)) {
            // 加油包购买个数限制， 不超过10个
            obj.max = 10 - matchRights.length ? 1 : 0;
          } else {
            const lastExpireDate = new Date(getLastExpireTime(matchRights));
            // 离会员过期还有多少天
            const diffDays = dayjs(rightData.vip_expire).diff(
              dayjs(lastExpireDate),
              "d",
              true
            );

            const matchSku = getMatchSku(good, diffDays);
            // 升级包购买限制，不超过会员周期
            obj.max =
              getMaxBuyNumber(matchRights, rightData.vips[0], matchSku) > 0
                ? 1
                : 0;

            obj.sku = matchSku;

            // 当剩余会员时长不足一个商品时间周期时
            if (obj.max < 1) {
              const calulateInfo = await orderCalculate({
                scene: "addition",
                skus: [
                  {
                    sku_id: matchSku.id,
                    quantity: 1,
                  },
                ],
                promotion: {
                  activities: [],
                  user_coupon_ids: [],
                },
              });

              if (matchSku.price == calulateInfo.actual_total_price) {
                obj.max = 1;
              }

              if (
                !calulateInfo.err &&
                matchSku.price != calulateInfo.actual_total_price
              ) {
                // 计算折算的天数
                calulateInfo.days = round(
                  getDiffDays(
                    rightData.vip_expire,
                    getLastExpireTime(matchRights)
                  ),
                  2
                );

                obj.max = 1;
                obj.calulate = calulateInfo;
              }
            }

            // 单品类不支持买升级包
            if (isSingleVipByUser(user)) {
              obj.max = 0;
            }
          }
          // 预先计算好支付信息
          obj.detail = getPayDetailByGood(good, 1, {
            start: getLastExpireTime(matchRights),
            ...(obj.calulate
              ? {
                  end: rightData.vip_expire,
                  extend: { price: obj.calulate.actual_total_price },
                  // 存在多个sku的情况，需要指定是哪个sku
                  skuCode: obj.sku.custom_code,
                }
              : {
                  // 存在多个sku的情况，需要指定是哪个sku
                  skuCode: obj.sku.custom_code,
                }),
          });
          obj.unitText = getSkuUnit(obj.sku);
          if (obj.max >= 1) result.push(obj);
        }

        if (result.length === 0) {
          HBMessage.info("下载次数已达上限");
          onCancel && onCancel();
        }

        if (!selectedPkg && defaultSelectedPkg) {
          const selectCard = result.find((c) =>
            defaultSelectedPkg.includes(c.goodCode)
          );
          setSelectedPkg(selectCard);
        }

        return result;
      }
    },
    { refreshDeps: [rightData, conf, goods] }
  );

  const payDetails = useMemo(() => {
    if (cardList?.length) {
      return [selectedPkg ? selectedPkg.detail : cardList[0].detail];
    }
    return null;
  }, [cardList, selectedPkg]);

  const download = useMemo(() => {
    if (!cardList || !cardList.length) {
      return null;
    }

    const currentDownload = rightData.downloadCount[0];

    const updateDownload = { ...currentDownload };
    const sku = (selectedPkg || cardList[0]).sku;
    const right_package_code = sku.right_packages[0].identification;
    const matchRights = rightData.effect_rights.filter(
      (right) => right_package_code === right.right_package_code
    );

    if (matchRights.length === 0) {
      if (isUpgradePkg(sku)) {
        updateDownload.left += isCompanyVipByUser(user) ? 20 : 10;
        updateDownload.quantity += isCompanyVipByUser(user) ? 20 : 10;
      }

      if (isUpgradePlusPkg(sku)) {
        updateDownload.left += 40;
        updateDownload.quantity += 40;
      }
    }

    if (isTemPkg(sku)) {
      updateDownload.left += 10;
    }

    const current = calculateDownload(currentDownload);
    const update = calculateDownload(updateDownload, {
      temUsage: current.temUsage,
    });

    return {
      current,
      update,
    };
  }, [cardList, rightData, selectedPkg, user]);

  return (
    <>
      <div className={styles.left}>
        {download && (
          <>
            <div className={styles.title}>增加下载数量</div>
            <div className={styles.subTitle}>
              当前剩余可用：{download.current.left}
            </div>
            <div className={styles.downloadDetail}>
              <div>
                每日下载数量：
                {download.current.dayCount - download.current.dayUsage}/
                {download.current.dayCount}
              </div>
              <div>
                24小时素材包：
                {download.current.temCount - download.current.temUsage}/
                {download.current.temCount}
              </div>
            </div>

            <div className={classNames(styles.subTitle, styles.primaryText)}>
              购买后立即可用：{download.update.left}
            </div>
            <div className={classNames(styles.downloadDetail)}>
              <div>
                每日下载数量：
                {download.update.dayCount - download.update.dayUsage}/
                {download.update.dayCount}
              </div>
              <div>
                24小时素材包：
                {download.update.temCount - download.update.temUsage}/
                {download.update.temCount}
              </div>
            </div>
          </>
        )}
      </div>
      <div className={styles.right} data-order-type="升级次数">
        <div className={styles.pkgs}>
          {cardList ? (
            <>
              {cardList.map((item, index) => (
                <Card
                  active={item === selectedPkg || (!selectedPkg && index === 0)}
                  conf={item}
                  key={item.goodCode}
                  calc={item.calulate}
                  price={
                    item.calulate
                      ? item.calulate.actual_total_price
                      : item.sku.price
                  }
                  onClick={() => !disabled && setSelectedPkg(item)}
                  expireTime={item.detail.expireTime}
                  disabled={disabled}
                />
              ))}
              {isPersonalVipByUser(user) && (
                <UpgradeCard conf={conf.upgrade} onUpdate={onUpdate} />
              )}
            </>
          ) : (
            <div className={styles.pkgCard}>
              <Skeleton.Avatar shape="circle" size={"large"} />
              <Skeleton active paragraph={4} />
            </div>
          )}
        </div>

        <PayBottom
          details={payDetails}
          scene="addition"
          onOrderDone={onOrderDone}
          onDisableChange={onDisableChangeWrap}
          onTransfer={onTransfer}
        />
      </div>
    </>
  );
}

function Card({ price, conf, active, expireTime, calc, onClick, disabled }) {
  return (
    <div
      className={classNames(styles.pkgCard, {
        [styles.active]: active,
        [styles.disabled]: disabled,
      })}
      onClick={onClick}
    >
      <Image
        width={48}
        {...gdImgSrcPolicy(conf.img[0].url, {
          displayWidth: 48,
          sharpen: 100,
          type: "png",
        })}
        alt="商品图片"
      />

      <div className={styles.title}>{conf.name}</div>
      <div className={styles.desc}>
        <span dangerouslySetInnerHTML={{ __html: conf.desc }}></span>
        {!conf.isTemPkg && `，${expireTime}到期`}
      </div>
      <div className={styles.price}>
        ¥<span>{price}</span>
        {conf.isTemPkg ||
          (calc ? `(${calc.days}天)` : `/${conf.unitText || "年"}`)}
      </div>
    </div>
  );
}

function UpgradeCard({ conf, onUpdate }) {
  return (
    <div className={styles.upgradeCard} onClick={onUpdate}>
      <div
        className={styles.title}
        dangerouslySetInnerHTML={{ __html: conf.title }}
      ></div>
      <div className={styles.desc}>{conf.desc}</div>
      <a>
        {conf.btnTxt} <HBIcon type="ic_open" />
      </a>
    </div>
  );
}

function calculateDownload(download, option) {
  const all = download.left + download.usage;
  const temCount = all - download.quantity;
  const temUsage =
    option?.temUsage ||
    (temCount >= download.usage ? download.usage : temCount);

  const dayCount = download.quantity;
  const dayUsage = download.usage - temUsage;
  return {
    all,
    dayCount,
    dayUsage,
    temUsage,
    temCount,
    left: download.left,
  };
}
